import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, X, Star, ArrowDownWideNarrow } from 'lucide-react';
import PatientDetailPanel from './PatientDetailPanel';
import useFavoritesStore from '../../../core/stores/favoritesStore';
import { getPrescriptionService } from '../../../domains/prescriptions';
import LogUtil from '../../../core/utils/LogUtil';
import AppUtil from '../../../core/utils/AppUtil';

/* ─────────────────────────────────────────
   필터 탭 (4개)
   ───────────────────────────────────────── */

const FILTER_TABS = [
  { id: 'all',     label: '전체' },
  { id: 'regular', label: '단골' },
  { id: 'today',   label: '오늘 방문' },
  { id: 'week',    label: '이번주' },
];

const GENDER_MAP = { M: '남', F: '여' };

/* "오늘 09:10", "어제 14:22", "5/3", "2026.04.27" 형태로 표시 */
const formatVisitTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.endsWith?.('Z') ? iso : iso + 'Z');
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (dDay.getTime() === today.getTime()) return `오늘 ${hh}:${mm}`;
  if (dDay.getTime() === yesterday.getTime()) return `어제 ${hh}:${mm}`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso.endsWith?.('Z') ? iso : iso + 'Z');
  const now = new Date();
  return d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
};

const isThisWeek = (iso) => {
  if (!iso) return false;
  const d = new Date(iso.endsWith?.('Z') ? iso : iso + 'Z');
  const now = new Date();
  const dayOfWeek = now.getDay() || 7; // 일=0 → 7 (월요일 시작)
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dayOfWeek - 1));
  monday.setHours(0, 0, 0, 0);
  return d >= monday;
};

/* ─────────────────────────────────────────
   좌측 환자 리스트
   ───────────────────────────────────────── */

// 가회원(앱 미가입 POS 환자)은 id·user_id 가 null → pos_patient_id 로 구분 (백엔드 11/05)
const patientKey = p => p.id || p.user_id || (p.pos_patient_id != null ? `pos-${p.pos_patient_id}` : null);

const PatientList = ({ patients, loading, error, search, setSearch, totalCount, counts, selectedId, onSelect, filter, setFilter }) => {
  // favoritesStore: 단골 환자 user_id 목록
  // 서버 응답에 is_vip 같은 단골 플래그가 없으므로 store만 단일 진실로 사용
  // 처방전 관리에서 토글한 즐겨찾기가 그대로 환자 목록에도 반영됨
  const favoriteUserIds = useFavoritesStore(s => s.userIds);
  const isFavoritePatient = (p) => {
    const uid = p.user_id ?? p.id;
    return uid != null && favoriteUserIds.includes(uid);
  };

  // 백엔드 counts{today, week, regular} (KST, regular=약국 즐겨찾기 환자)
  const filterCounts = { all: totalCount, ...counts };

  const filtered = useMemo(() => {
    switch (filter) {
      case 'regular':
        return patients.filter(isFavoritePatient);
      case 'today':
        return patients.filter(p => isToday(p.last_visit || p.last_visit_at));
      case 'week':
        return patients.filter(p => isThisWeek(p.last_visit || p.last_visit_at));
      default:
        return patients;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patients, filter, favoriteUserIds]);

  return (
    <section className="flex w-[500px] shrink-0 flex-col bg-white border-r border-zinc-200">
      <div className="flex flex-col gap-4 px-6 pt-6">
          <div className="flex flex-wrap gap-2 items-start">
            {FILTER_TABS.map(t => {
              const active = filter === t.id;
              const count = filterCounts[t.id];
              return (
                <button
                  key={t.id}
                  onClick={() => setFilter(t.id)}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md whitespace-nowrap shrink-0 transition-colors"
                  style={{
                    fontSize: 'var(--text-15)',
                    lineHeight: '22px',
                    letterSpacing: '-0.3px',
                    background: active ? '#e5f2ff' : '#f4f4f5',
                    color: active ? '#007aff' : '#71717a',
                  }}
                >
                  <span style={{ fontWeight: active ? 600 : 500 }}>{t.label}</span>
                  {count != null && (
                    <span style={{ fontWeight: 500 }}>{count.toLocaleString()}</span>
                  )}
                </button>
              );
            })}
        </div>

        <div
          className="flex h-10 items-center gap-4 w-full"
          style={{
            background: '#fafafa',
            border: '1px solid #e4e4e7',
            borderRadius: 8,
            padding: '8px 12px',
          }}
        >
          <div className="flex flex-1 min-w-0 items-center gap-2">
            <Search size={20} className="text-[#71717a] shrink-0" strokeWidth={1.75} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="설명, 대상으로 검색"
              className="flex-1 min-w-0 bg-transparent outline-none"
              style={{
                fontSize: 'var(--text-14)',
                color: '#18181b',
                lineHeight: '20px',
                letterSpacing: '-0.28px',
              }}
            />
          </div>
          {search && (
            <button type="button" onClick={() => setSearch('')} aria-label="검색어 지우기">
              <X size={24} className="text-zinc-500" strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>

      <div
        className="mb-6 mt-4 flex flex-col flex-1 min-h-0 overflow-hidden"
      >
        {/* 메타 헤더 — Figma 767:28209: bg-#fafafa border-y p-8 */}
        <div
          className="mx-6 flex items-center justify-between border-y border-[rgba(228,228,231,0.36)]"
          style={{ background: '#fafafa', padding: 8 }}
        >
          <div
            className="flex items-center gap-1"
            style={{ padding: 8, color: '#3f3f46', fontSize: 'var(--text-14)', lineHeight: '20px', letterSpacing: '-0.28px' }}
          >
            <span style={{ fontWeight: 500 }}>총</span>
            <span style={{ fontWeight: 600 }}>{totalCount.toLocaleString()}명</span>
          </div>
          <button
            className="flex items-center gap-1"
            style={{ color: '#71717a', fontSize: 'var(--text-12)', lineHeight: '18px' }}
          >
            <span>최신 방문 순</span>
            <ArrowDownWideNarrow size={16} strokeWidth={1.5} />
          </button>
        </div>

        {/* 환자 리스트 */}
        {/* Figma 767:28230 스크롤 인디케이터 10px #e4e4e7 r14, right 8 — gutter 고정으로 행 폭 452 유지 */}
        <div className="ml-6 mr-[11px] pr-[3px] [scrollbar-gutter:stable] flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-[10px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-[14px] [&::-webkit-scrollbar-thumb]:bg-[#e4e4e7]">
          {filtered.length === 0 ? null : (
            <ul>
              {filtered.map(p => {
                const id = patientKey(p);
                const selected = selectedId === id;
                const visitTimeText = formatVisitTime(p.last_visit || p.last_visit_at || p.visit_time);
                const isFav = isFavoritePatient(p);
                return (
                  <li
                    key={id}
                    onClick={() => onSelect(id)}
                    className="flex h-[66px] flex-col justify-center gap-1 cursor-pointer border-b border-[rgba(228,228,231,0.36)] transition-colors"
                    style={{
                      padding: 12,
                      background: selected ? '#f3f9ff' : 'transparent',
                    }}
                  >
                    {/* row 1: 이름 + 별 + 성별·나이 / 우측 시간 */}
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-1 min-w-0">
                        <div className="flex items-center" style={{ gap: 2 }}>
                          <span
                            className="truncate"
                            style={{
                              fontSize: 'var(--text-14)',
                              fontWeight: 500,
                              color: '#3f3f46',
                              lineHeight: '20px',
                              letterSpacing: '-0.28px',
                            }}
                          >
                            {p.name || '-'}
                          </span>
                          {isFav && (
                            <div className="flex items-start self-stretch shrink-0">
                              <Star size={12} className="fill-amber-400 text-amber-400 shrink-0" />
                            </div>
                          )}
                        </div>
                        <span
                          className="whitespace-nowrap"
                          style={{
                            fontSize: 'var(--text-14)',
                            fontWeight: 500,
                            color: '#71717a',
                            lineHeight: '20px',
                            letterSpacing: '-0.28px',
                          }}
                        >
                          {[GENDER_MAP[p.gender] || p.gender, p.age != null ? `${p.age}세` : null]
                            .filter(Boolean)
                            .map(v => ` · ${v}`)
                            .join('')}
                        </span>
                      </div>
                      {visitTimeText && (
                        <span
                          className="whitespace-nowrap shrink-0"
                          style={{
                            fontSize: 'var(--text-12)',
                            color: '#71717a',
                            lineHeight: '18px',
                          }}
                        >
                          {visitTimeText}
                        </span>
                      )}
                    </div>

                    {/* row 2: 회원상태 · 전화 · 방문횟수 */}
                    <div
                      className="flex items-center gap-1 whitespace-nowrap"
                      style={{ fontSize: 'var(--text-12)', lineHeight: '18px' }}
                    >
                      <span
                        style={{
                          fontWeight: 500,
                          color: (p.is_app_member ?? p.app_installed) ? '#1d356d' : '#a1a1aa',
                          width: 32,
                        }}
                      >
                        {(p.is_app_member ?? p.app_installed) ? '앱회원' : '비회원'}
                      </span>
                      <span style={{ color: '#71717a' }}>·</span>
                      <span style={{ color: '#71717a' }}>{p.phone_number || p.phone ? AppUtil.formatPhoneNumber(p.phone_number || p.phone) : '-'}</span>
                      {p.visit_count != null && (
                        <>
                          <span style={{ color: '#71717a' }}>·</span>
                          <span style={{ color: '#71717a' }}>방문 {p.visit_count}회</span>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   PatientPage
   ───────────────────────────────────────── */

const PatientPage = () => {
  const location = useLocation();
  const presetName = location.state?.patientName;

  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState(presetName || '');
  const [filter, setFilter] = useState('all');
  const [patientList, setPatientList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchTimer = useRef(null);

  const fetchPatients = useCallback(async (searchQuery = '') => {
    try {
      setLoading(true);
      setError(null);
      const service = getPrescriptionService();
      const params = { page: 1, size: 100 };
      if (searchQuery) params.search = searchQuery;
      const res = await service.getPatients(params);
      const data = res?.data?.data || res?.data || [];
      const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
      setPatientList(items);
      setTotalCount(data.total_count ?? data.total ?? items.length);
      setCounts(data.counts ?? null);
    } catch (err) {
      LogUtil.error('[PatientPage] 환자 목록 조회 실패:', err);
      setError(err.message || '환자 목록을 불러올 수 없습니다.');
      setPatientList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // search 디바운스
  useEffect(() => {
    if (!search) {
      fetchPatients();
      return;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchPatients(search);
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [search, fetchPatients]);

  // preset 이름으로 자동 선택 (다른 페이지에서 환자 선택해서 진입한 경우)
  useEffect(() => {
    if (presetName && patientList.length > 0) {
      const target = patientList.find(p => p.name === presetName);
      if (target) setSelectedId(patientKey(target));
    }
  }, [patientList, presetName]);

  const selectedPatient = useMemo(() => {
    if (!selectedId) return null;
    return patientList.find(p => patientKey(p) === selectedId);
  }, [patientList, selectedId]);

  return (
    /* Figma 418:4461 — 환자 목록 페이지: 카드/padding 없이 화면 전체 사용, 좌패널 border-r로 분리 */
    <div className="flex h-full overflow-hidden bg-white">
      <PatientList
        patients={patientList}
        loading={loading}
        error={error}
        search={search}
        setSearch={setSearch}
        totalCount={totalCount}
        counts={counts}
        selectedId={selectedId}
        onSelect={setSelectedId}
        filter={filter}
        setFilter={setFilter}
      />

      <section className="flex flex-1 flex-col min-w-0 bg-white">
        {selectedId ? (
          <PatientDetailPanel
            userId={selectedPatient?.user_id ?? selectedPatient?.id ?? null}
            posPatientId={selectedPatient?.pos_patient_id ?? null}
            patient={selectedPatient}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-14 text-zinc-400">
            왼쪽에서 환자를 선택하세요
          </div>
        )}
      </section>
    </div>
  );
};

export default PatientPage;
